import { type Signal, signal } from '@preact/signals'
import type { DataCaptureView } from '@scandit/web-datacapture-core'
import { FrameSourceState } from '@scandit/web-datacapture-core'
import {
  AdaptiveRecognitionMode,
  type LabelCapture,
  type LabelCaptureSettings,
  LabelDateComponentFormat,
} from '@scandit/web-datacapture-label'
import type { BuildSettingsContext, PresetController, Teardown } from './labelCapture/PresetController'

export type LabelPreset = 'perishableProduct' | 'smartphoneBox' | 'vinCode' | 'shoppingReceipt' | 'priceShelfLabel'
export type DateFormat = 'mdy' | 'dmy' | 'ymd'

export const LABEL_PRESET_OPTIONS: {
  key: LabelPreset
  text: string
  value: LabelPreset
}[] = [
  {
    key: 'perishableProduct',
    text: 'Perishable Product',
    value: 'perishableProduct',
  },
  { key: 'smartphoneBox', text: 'Smartphone Box', value: 'smartphoneBox' },
  { key: 'vinCode', text: 'VIN code', value: 'vinCode' },
  {
    key: 'shoppingReceipt',
    text: 'Shopping Receipt',
    value: 'shoppingReceipt',
  },
  {
    key: 'priceShelfLabel',
    text: 'Price Shelf Label',
    value: 'priceShelfLabel',
  },
]

export const DATE_FORMAT_OPTIONS: {
  key: DateFormat
  text: string
  value: DateFormat
}[] = [
  { key: 'mdy', text: 'Month Day Year', value: 'mdy' },
  { key: 'dmy', text: 'Day Month Year', value: 'dmy' },
  { key: 'ymd', text: 'Year Month Day', value: 'ymd' },
]

const DATE_FORMAT_MAP: Record<DateFormat, LabelDateComponentFormat> = {
  mdy: LabelDateComponentFormat.MDY,
  dmy: LabelDateComponentFormat.DMY,
  ymd: LabelDateComponentFormat.YMD,
}

export interface LabelCaptureSettingsStoreOptions {
  controllers?: readonly PresetController[]
  reconfigureMode?: (settings: LabelCaptureSettings) => Promise<LabelCapture | undefined>
  onPresetCommitted?: (preset: LabelPreset) => void
  onBeginEditing?: () => void
  onEndEditing?: () => void
}

export class LabelCaptureSettingsStore {
  public selectedPreset: Signal<LabelPreset> = signal('perishableProduct')
  public selectedDateFormat: Signal<DateFormat> = signal('mdy')
  public adaptiveRecognition: Signal<boolean> = signal(false)
  public acceptPartialDates: Signal<boolean> = signal(true)
  public pricesIncludeDecimals: Signal<boolean> = signal(true)

  private mode: LabelCapture | undefined
  private view: DataCaptureView | undefined
  private readonly controllers: Map<LabelPreset, PresetController>
  private readonly reconfigureModeFn:
    ((settings: LabelCaptureSettings) => Promise<LabelCapture | undefined>) | undefined
  private readonly onPresetCommitted: ((preset: LabelPreset) => void) | undefined
  private readonly onBeginEditingFn: (() => void) | undefined
  private readonly onEndEditingFn: (() => void) | undefined
  private currentTeardown: Teardown | undefined
  private presetSnapshot: LabelPreset | undefined
  private lastAppliedPreset: LabelPreset | undefined
  private cameraStateBeforeEditing: FrameSourceState | undefined
  private hasChanges = false
  private isEditing = false

  constructor(options: LabelCaptureSettingsStoreOptions = {}) {
    this.controllers = new Map((options.controllers ?? []).map((c) => [c.preset, c]))
    this.reconfigureModeFn = options.reconfigureMode
    this.onPresetCommitted = options.onPresetCommitted
    this.onBeginEditingFn = options.onBeginEditing
    this.onEndEditingFn = options.onEndEditing
  }

  /** Wires the runtime SDK objects after the LabelCapture mode has been created. */
  attach(mode: LabelCapture, view: DataCaptureView) {
    this.mode = mode
    this.view = view
  }

  private buildSettingsContext(): BuildSettingsContext {
    return {
      dateFormat: DATE_FORMAT_MAP[this.selectedDateFormat.peek()],
      adaptiveRecognitionMode: this.adaptiveRecognition.peek()
        ? AdaptiveRecognitionMode.Auto
        : AdaptiveRecognitionMode.Off,
      acceptPartialDates: this.acceptPartialDates.peek(),
      pricesIncludeDecimals: this.pricesIncludeDecimals.peek(),
    }
  }

  async applyOverlayForCurrentPreset(): Promise<void> {
    if (!this.mode || !this.view) return

    await this.currentTeardown?.()
    this.currentTeardown = undefined

    const preset = this.selectedPreset.peek()
    const controller = this.controllers.get(preset)
    if (!controller) {
      throw new Error(`No controller registered for preset "${preset}"`)
    }

    this.currentTeardown = await controller.activate(this.mode, this.view)
    this.lastAppliedPreset = preset
  }

  async buildSettings(): Promise<LabelCaptureSettings> {
    const preset = this.selectedPreset.peek()
    const controller = this.controllers.get(preset)
    if (!controller) {
      throw new Error(`No controller registered for preset "${preset}"`)
    }
    return controller.buildSettings(this.buildSettingsContext())
  }

  setPreset(preset: LabelPreset): void {
    const current = this.selectedPreset.peek()
    if (current === preset) return
    if (current === 'shoppingReceipt') {
      this.adaptiveRecognition.value = false
    }
    this.selectedPreset.value = preset
    this.hasChanges = true
  }

  setDateFormat(dateFormat: DateFormat): void {
    if (this.selectedDateFormat.peek() === dateFormat) return
    this.selectedDateFormat.value = dateFormat
    this.hasChanges = true
  }

  setAdaptiveRecognition(enabled: boolean): void {
    if (this.adaptiveRecognition.peek() === enabled) return
    this.adaptiveRecognition.value = enabled
    this.hasChanges = true
  }

  setAcceptPartialDates(enabled: boolean): void {
    if (this.acceptPartialDates.peek() === enabled) return
    this.acceptPartialDates.value = enabled
    this.hasChanges = true
  }

  setPricesIncludeDecimals(enabled: boolean): void {
    if (this.pricesIncludeDecimals.peek() === enabled) return
    this.pricesIncludeDecimals.value = enabled
    this.hasChanges = true
  }

  async pauseScanning(): Promise<void> {
    if (!this.mode) return
    const controller = this.controllers.get(this.selectedPreset.peek())
    await controller?.pause?.(this.mode)
  }

  async resumeScanning(): Promise<void> {
    if (!this.mode) return
    const controller = this.controllers.get(this.selectedPreset.peek())
    await controller?.resume?.(this.mode)
  }

  async beginEditing(): Promise<void> {
    if (this.isEditing) return
    this.isEditing = true
    this.onBeginEditingFn?.()
    this.presetSnapshot = this.selectedPreset.peek()
    const frameSource = this.mode?.context?.frameSource
    this.cameraStateBeforeEditing = frameSource?.getCurrentState()
    await frameSource?.switchToDesiredState(FrameSourceState.Standby)
  }

  requiresAdaptiveRecognitionEnabled(): boolean {
    return this.selectedPreset.peek() === 'shoppingReceipt' && !this.adaptiveRecognition.peek()
  }

  revertPresetToSnapshot(): void {
    if (this.presetSnapshot === undefined) return
    this.setPreset(this.presetSnapshot)
  }

  async endEditing(): Promise<void> {
    if (!this.isEditing) return
    this.isEditing = false

    try {
      if (this.hasChanges && this.view) {
        const settings = await this.buildSettings()
        const newPreset = this.selectedPreset.peek()
        const presetChanged = this.lastAppliedPreset !== newPreset

        // Always: teardown active overlay → applySettings → re-attach overlay below.
        // Mirrors the debug app pattern (LabelCaptureModeViewModel.applyLabelCaptureSettings).
        // Recreating the mode via reconfigureModeFn on preset change works for
        // simple field types but breaks dedicated barcode fields (IMEI/Serial) —
        // their OCR text recognizer doesn't survive the removeAllModes + forContext
        // round-trip the way it does when applySettings swaps fields on a live mode.
        await this.currentTeardown?.()
        this.currentTeardown = undefined
        if (this.mode) {
          await this.mode.applySettings(settings)
        }

        await this.applyOverlayForCurrentPreset()

        this.hasChanges = false

        if (presetChanged) {
          this.onPresetCommitted?.(newPreset)
        }
      } else if (this.cameraStateBeforeEditing != null) {
        // No changes were applied — restore the camera to the state it was in
        // before beginEditing forced it into Standby.
        await this.mode?.context?.frameSource?.switchToDesiredState(this.cameraStateBeforeEditing)
      }
    } catch (error) {
      console.error(error)
    } finally {
      this.isEditing = false
      this.hasChanges = false
      this.cameraStateBeforeEditing = undefined
      this.onEndEditingFn?.()
    }
  }
}
